"use client";

import { useLang } from "@/contexts/language-context";
import { motion } from "framer-motion";
import Image from "next/image";
import { ArrowRight, CheckCircle2, WifiOff } from "lucide-react";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { EmailInput } from "@/components/ui";
import AppStoreBadges from "./AppStoreBadges";

export default function HeroSection() {
  const { t, lang } = useLang();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  const handleSignup = async () => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      return;
    }
    setLoading(true);
    // Fire-and-forget lead capture, then go straight to signup
    fetch("/api/leads", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: trimmed, source: "hero", lang }),
    }).catch(() => {})
    router.push(`/signup?email=${encodeURIComponent(trimmed)}`);
  };

  return (
    <section id="hero" className="relative pt-36 pb-16 px-6 overflow-hidden">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/marketing-home.mp4" type="video/mp4" />
      </video>

      {/* White overlay at 80% — video shows through */}
      <div className="absolute inset-0 bg-[#FAFAFA]/80 pointer-events-none" />

      {/* Background glows */}
      <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-redy-orange/6 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-redy-red/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

        {/* ── Left: copy ─────────────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <div className="inline-flex items-center gap-2 bg-white border border-black/5 px-4 py-2 rounded-full text-xs font-bold text-redy-charcoal mb-8 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-redy-orange animate-pulse" />
            {t("One platform · two verticals", "Una plataforma · dos verticales")}
          </div>

          <h1 className="text-[40px] sm:text-5xl md:text-[72px] font-black leading-[1.02] tracking-tight mb-6">
            <span className="text-redy-charcoal">{t("Charge. Run.", "Cobra. Opera.")}</span>
            <br />
            <span className="redy-text-gradient">{t("Grow.", "Crece.")}</span>
          </h1>

          <p className="text-lg md:text-xl text-redy-secondary max-w-[520px] leading-relaxed mb-3 font-medium">
            {t(
              "The system that powers your restaurant, bar, or nightclub — from the first order to the final cash-out, all in one place.",
              "El sistema que mueve tu restaurante, bar o nightclub — del primer pedido al cierre de caja, en un solo lugar."
            )}
          </p>

          <p className="text-base md:text-lg text-redy-secondary max-w-[520px] leading-relaxed mb-10 font-medium">
            <span className="font-bold text-redy-charcoal">{t("One platform", "Una sola plataforma")}</span>
            {t(" for your whole business — not five separate apps.", " para todo tu negocio — no cinco apps distintas.")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-[500px]">
              <EmailInput
                value={email}
                onChange={setEmail}
                placeholder={t("your@business.com", "tu@negocio.com")}
                disabled={loading}
                inputClassName="flex-1 px-6 py-4 bg-white border border-black/10 rounded-full text-base font-medium outline-none focus:border-redy-orange focus:ring-2 focus:ring-redy-orange/20 transition-all shadow-sm disabled:opacity-60"
              />
              <button
                onClick={handleSignup}
                disabled={loading}
                className="group px-8 py-4 bg-redy-charcoal text-white font-bold rounded-full shadow-lg hover:bg-black transition-all active:scale-95 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading
                  ? t("Sending…", "Enviando…")
                  : t("Get started free", "Comenzar gratis")}
                {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
              </button>
            </div>

          <div className="mt-6 flex flex-wrap gap-6">
            <div className="flex items-center gap-2 text-[13px] font-bold text-redy-secondary">
              <CheckCircle2 className="w-4 h-4 text-redy-orange shrink-0" />
              {t("Month to month, no long-term contract", "Mes a mes, sin contrato a largo plazo")}
            </div>
            <div className="flex items-center gap-2 text-[13px] font-bold text-redy-secondary">
              <WifiOff className="w-4 h-4 text-redy-orange shrink-0" />
              {t("Cash sales work offline", "Ventas en efectivo sin internet")}
            </div>
            <div className="flex items-center gap-2 text-[13px] font-bold text-redy-secondary">
              <CheckCircle2 className="w-4 h-4 text-redy-orange shrink-0" />
              {t("Dual pricing built in", "Dual pricing incorporado")}
            </div>
          </div>

          <div className="mt-8">
            <div className="text-[11px] font-bold tracking-widest uppercase text-redy-secondary/60 mb-3">
              {t(
                "Download the app on your iPad or Android tablet",
                "Descarga la app en tu iPad o tablet Android"
              )}
            </div>
            <AppStoreBadges height={40} />
          </div>
        </motion.div>

        {/* ── Right: product screenshot ──────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative flex items-center justify-center"
        >
          {/* Glow — large diffuse, not box-shaped */}
          <div className="absolute w-[700px] h-[500px] bg-redy-orange/12 rounded-full blur-[120px] -z-10" />

          {/* Real product screenshot — LCP image of the home page, so it's
              served via next/image (responsive AVIF/WebP + preload) instead of
              the raw 3.4 MB PNG. Intrinsic size 3000×2000 keeps the 3:2 ratio
              (no CLS); `priority` preloads it as the hero. */}
          <Image
            src="/hero-product.png"
            alt="Redy POS — tablet, desktop y dashboard móvil"
            width={3000}
            height={2000}
            priority
            sizes="(min-width: 1024px) 600px, 100vw"
            className="w-full h-auto rounded-[20px] shadow-[0_32px_80px_rgba(0,0,0,0.18)]"
          />

          {/* Floating: dual pricing */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="absolute -left-10 top-[28%] bg-white rounded-[20px] shadow-xl border border-black/5 px-4 py-3 hidden lg:flex items-center gap-3"
          >
            <div className="w-10 h-10 bg-redy-green/10 rounded-full flex items-center justify-center shrink-0">
              <span className="text-[11px] font-black text-redy-green">Dual</span>
            </div>
            <div>
              <div className="text-[12px] font-black text-redy-charcoal leading-tight">{t("Dual Pricing", "Dual Pricing")}</div>
              <div className="text-[10px] font-bold text-redy-secondary">{t("Card payers cover the fee", "La tarjeta cubre el fee")}</div>
            </div>
          </motion.div>

          {/* Floating: order paid */}
          <motion.div
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="absolute -right-8 bottom-[18%] bg-white rounded-[20px] shadow-xl border border-black/5 p-4 min-w-[150px] hidden lg:block"
          >
            <div className="text-[10px] font-bold text-redy-secondary mb-1">Order #104</div>
            <div className="text-[20px] font-black text-redy-charcoal">$24.50</div>
            <div className="mt-2 inline-block px-3 py-1 bg-redy-green text-white text-[9px] font-black rounded-full uppercase tracking-wider">
              {t("Paid", "Pagado")}
            </div>
          </motion.div>

          {/* Floating: savings badge */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.3 }}
            className="absolute -top-6 right-[15%] bg-redy-orange text-white rounded-[20px] shadow-lg px-4 py-3 hidden lg:block"
          >
            <div className="text-[10px] font-bold text-white/70 leading-tight">
              {t("Example: fees covered this month", "Ejemplo: fees cubiertos este mes")}
            </div>
            <div className="text-[18px] font-black leading-tight">$1,200</div>
          </motion.div>
        </motion.div>
      </div>

      {/* ── Payment logo carousel — full viewport width ───────────────────────── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="relative z-10 mt-16 -mx-6"
      >
        <p className="text-[11px] font-bold tracking-widest uppercase text-redy-secondary/40 text-center mb-5">
          {t("Accepts all major payments", "Acepta todos los métodos de pago")}
        </p>
        <div
          className="overflow-hidden"
          style={{
            maskImage: "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
          }}
        >
          {/* 4 sets of logos so the track is always wider than the viewport */}
          <div className="flex w-max animate-marquee hover:[animation-play-state:paused]">
            {Array.from({ length: 4 }).flatMap((_, set) =>
              [
                { src: "/logo-visa.png",       alt: "Visa",       h: "h-6" },
                { src: "/logo-mastercard.png", alt: "Mastercard", h: "h-7" },
                { src: "/logo-amex.png",       alt: "Amex",       h: "h-6" },
                { src: "/logo-discover.png",   alt: "Discover",   h: "h-6" },
                { src: "/logo-applepay.png",   alt: "Apple Pay",  h: "h-6" },
                { src: "/logo-googlepay.png",  alt: "Google Pay", h: "h-6" },
                { src: "/logo-north.png",      alt: "North",      h: "h-6" },
              ].map((logo, i) => (
                <div key={`${set}-${i}`} className="flex items-center justify-center mx-14 shrink-0 opacity-40 grayscale">
                  {/* Decorative trust strip, lazy-loaded and duplicated 4× for the
                      marquee — next/image adds no real value here, so kept as <img>. */}
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={logo.src} alt={logo.alt} className={`${logo.h} w-auto object-contain`} loading="lazy" />
                </div>
              ))
            )}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
